import CryptoJS from 'crypto-js';

import { Config } from 'config';
import { User, CloverPayment } from 'types';

const userKey = 'authUser';
const darkThemeKey = 'isDarkTheme';
const rememberedEmailKey = 'rememberedEmail';
const externalPaymentIdKey = 'externalPaymentId';
const selectedCloverDevicesKey = 'selectedCloverDevices';
const createShippingCloverPaymentsKey = 'createShippingCloverPayments';

type ExternalPaymentIds = Record<string, string>;
type CreateShippingCloverPayments = Record<string, CloverPayment[]>;

export interface Tokens {
  token?: string;
  refreshToken?: string;
}

export type TokenKey = 'accessToken' | 'refreshToken';

const accessTokenKey: TokenKey = 'accessToken';
const refreshTokenKey: TokenKey = 'refreshToken';
const tokenKeys: TokenKey[] = [accessTokenKey, refreshTokenKey];

export const rememberEmail = (remember: boolean, email?: string) => {
  if (remember && email) {
    localStorage.setItem(rememberedEmailKey, email);
  } else {
    localStorage.removeItem(rememberedEmailKey);
  }
};

export const getRememberedEmail = () =>
  localStorage.getItem(rememberedEmailKey);

export const saveDarkTheme = (isDarkTheme?: boolean) => {
  if (typeof isDarkTheme === 'boolean') {
    localStorage.setItem(darkThemeKey, String(isDarkTheme));
  } else {
    localStorage.removeItem(darkThemeKey);
  }
};

export const getIsDarkTheme = (): boolean | undefined => {
  const isDarkTheme = localStorage.getItem(darkThemeKey);
  if (isDarkTheme === null) {
    return undefined;
  }
  return isDarkTheme === 'true';
};

export const getSelectedCloverDevices = (): Record<number, number> => {
  try {
    const stored = localStorage.getItem(selectedCloverDevicesKey);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

export const saveSelectedCloverDevices = (devices: Record<number, number>) => {
  localStorage.setItem(selectedCloverDevicesKey, JSON.stringify(devices));
};

export const clearAllSelectedCloverDevices = () =>
  localStorage.removeItem(selectedCloverDevicesKey);

export const getStoredCreateShippingCloverPayments =
  (): CreateShippingCloverPayments => {
    try {
      const existing = localStorage.getItem(createShippingCloverPaymentsKey);
      return existing ? JSON.parse(existing) : {};
    } catch {
      return {};
    }
  };

export const saveCreateShippingCloverPayment = (
  checkoutId: string | number,
  payment: CloverPayment
) => {
  const parsed = getStoredCreateShippingCloverPayments();
  const existingPayments = parsed[checkoutId] ?? [];

  if (!existingPayments.some((item) => item.paymentId === payment.paymentId)) {
    parsed[checkoutId] = [...existingPayments, payment];
    localStorage.setItem(
      createShippingCloverPaymentsKey,
      JSON.stringify(parsed)
    );
  }
};

export const getCreateShippingCloverPayments = (
  checkoutId: string | number
): CloverPayment[] => {
  const parsed = getStoredCreateShippingCloverPayments();
  return parsed[String(checkoutId)] ?? [];
};

export const clearCreateShippingCloverPayments = (
  checkoutId?: string | number
) => {
  if (!checkoutId) {
    localStorage.removeItem(createShippingCloverPaymentsKey);
    return;
  }

  const parsed = getStoredCreateShippingCloverPayments();
  delete parsed[String(checkoutId)];

  if (!Object.keys(parsed).length) {
    localStorage.removeItem(createShippingCloverPaymentsKey);
    return;
  }

  localStorage.setItem(createShippingCloverPaymentsKey, JSON.stringify(parsed));
};

export const removeCreateShippingCloverPayment = (
  checkoutId: string | number,
  paymentId?: string
) => {
  if (!paymentId) {
    return;
  }

  const parsed = getStoredCreateShippingCloverPayments();
  const key = String(checkoutId);
  const existingPayments = parsed[key] ?? [];

  const filteredPayments = existingPayments.filter(
    (item) => item.paymentId !== paymentId
  );

  if (filteredPayments.length) {
    parsed[key] = filteredPayments;
  } else {
    delete parsed[key];
  }

  if (!Object.keys(parsed).length) {
    localStorage.removeItem(createShippingCloverPaymentsKey);
    return;
  }

  localStorage.setItem(createShippingCloverPaymentsKey, JSON.stringify(parsed));
};

export const saveExternalPaymentId = (eventId: string, paymentId: string) => {
  const existing = localStorage.getItem(externalPaymentIdKey);
  const parsed: ExternalPaymentIds = existing ? JSON.parse(existing) : {};

  parsed[eventId] = paymentId;

  localStorage.setItem(externalPaymentIdKey, JSON.stringify(parsed));
};

export const getExternalPaymentId = (eventId: string) => {
  const existing = localStorage.getItem(externalPaymentIdKey);
  if (!existing) {
    return;
  }

  const parsed: ExternalPaymentIds = JSON.parse(existing);
  return parsed[eventId];
};

export const clearExternalPaymentId = (eventId?: string) => {
  if (!eventId) {
    localStorage.removeItem(externalPaymentIdKey);
    return;
  }

  const existing = localStorage.getItem(externalPaymentIdKey);
  if (!existing) {
    return;
  }

  const parsed: ExternalPaymentIds = JSON.parse(existing);
  delete parsed[eventId];

  if (Object.keys(parsed).length === 0) {
    localStorage.removeItem(externalPaymentIdKey);
  } else {
    localStorage.setItem(externalPaymentIdKey, JSON.stringify(parsed));
  }
};

// An async action may finish after another checkout has replaced this hint.
// Never let that earlier action remove the newer payment reference.
export const clearExternalPaymentIdIfMatches = (
  eventId: string,
  expectedPaymentId: string
) => {
  if (getExternalPaymentId(eventId) !== expectedPaymentId) return false;
  clearExternalPaymentId(eventId);
  return true;
};

export const clearTokens = () =>
  tokenKeys.forEach((tokenKey) => localStorage.removeItem(tokenKey));

export const clearUser = () => localStorage.removeItem(userKey);

export const clearAll = () => {
  clearTokens();
  clearUser();
  clearExternalPaymentId();
  clearAllSelectedCloverDevices();
  clearCreateShippingCloverPayments();
};

export const saveToken = (tokenKey: TokenKey, token: string) =>
  localStorage.setItem(
    tokenKey,
    CryptoJS.AES.encrypt(token, Config.cryptoKey).toString()
  );

export const saveTokens = ({ token, refreshToken }: Tokens) => {
  if (token) {
    saveToken('accessToken', token);
  }
  if (refreshToken) {
    saveToken('refreshToken', refreshToken);
  }
};

export const getToken = (tokenKey: TokenKey = accessTokenKey) => {
  const token = localStorage.getItem(tokenKey);

  if (!token) {
    return;
  }

  try {
    return CryptoJS.AES.decrypt(token, Config.cryptoKey).toString(
      CryptoJS.enc.Utf8
    );
  } catch {
    clearAll();
  }
};

export const decryptToken = (token: string) =>
  CryptoJS.AES.decrypt(token, Config.cryptoKey).toString(CryptoJS.enc.Utf8);

export const saveUser = (user: User) =>
  localStorage.setItem(userKey, JSON.stringify(user));

export const getUser = (): User | undefined => {
  const user = localStorage.getItem(userKey);
  if (!user) {
    return undefined;
  }
  return JSON.parse(user);
};

export const getTokens = () => ({
  token: getToken('accessToken'),
  refreshToken: getToken('refreshToken'),
});
